import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "@uidotdev/usehooks";
import { format } from "date-fns";
import { cn } from "~/lib/utils";
import { useSelector } from "~/redux/store";
import { restApi } from "~/redux/restApi";
import { DEFAULT_API_PAYLOAD } from "~/lib/constants";
import { AIIcon } from "~/assets/icons";
import { AISearchResponse } from "~/components/shared/ai-search/types.ts";
import { useFeatureFlag } from "~/hooks/use-feature-flag";
import "../../styles/ai-search.css";

export const AnimatedSearch = () => {
  const isAiEnabled = useFeatureFlag("isAiUseEnabled");
  const navigate = useNavigate();
  const userId = useSelector((state) => state.persistedReducer.auth.userId);
  const { data } = restApi.useProfileQuery(
    { ...DEFAULT_API_PAYLOAD, id: userId! },
    { skip: !userId },
  );

  const profile = data?.result;
  const firstName = profile?.firstName || "";

  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedQuery = useDebounce(searchValue, 2500); // 2-3s typing pause
  const [displayText, setDisplayText] = useState("Veiligh Inteligence Search");
  const [lastSearchedValue, setLastSearchedValue] = useState("");
  const [shouldFlash, setShouldFlash] = useState(false);

  const [getAIResponse, { data: queryResults, isLoading }] =
    restApi.useLazyGetAISearchQuery();

  const targetText = isHovered
    ? `How can I help, ${firstName || "User"}?`
    : "Veiligh Inteligence Search";

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const tick = () => {
      if (displayText === targetText) return;

      const shouldType = targetText.startsWith(displayText);
      if (shouldType) {
        // Type forward at slightly faster human reading speed (~75ms)
        const nextChar = targetText[displayText.length];
        setDisplayText((prev) => prev + nextChar);
        timer = setTimeout(tick, 75);
      } else {
        // Backspace character-by-character (~40ms)
        setDisplayText((prev) => prev.slice(0, -1));
        timer = setTimeout(tick, 40);
      }
    };

    timer = setTimeout(tick, isHovered ? 75 : 40);
    return () => clearTimeout(timer);
  }, [displayText, targetText, isHovered]);

  const triggerSearch = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (trimmed.length > 0 && trimmed !== lastSearchedValue) {
        getAIResponse({
          ...DEFAULT_API_PAYLOAD,
          queryPrompt: trimmed,
        });
        setLastSearchedValue(trimmed);
        setShouldFlash(true);
      }
    },
    [getAIResponse, lastSearchedValue],
  );

  useEffect(() => {
    const query = debouncedQuery.trim();
    if (query.length > 0 && query !== lastSearchedValue) {
      triggerSearch(query);
    }
  }, [debouncedQuery, lastSearchedValue, triggerSearch]);

  useEffect(() => {
    if (searchValue.trim().length === 0) {
      setLastSearchedValue("");
      setShouldFlash(false);
    }
  }, [searchValue]);

  useEffect(() => {
    if (isHovered || isFocused) {
      setShouldFlash(false);
    }
  }, [isHovered, isFocused]);

  const isWaitingToSearch = searchValue.trim() !== lastSearchedValue;

  const isAggregate = (item: any) => {
    return !item?.id && !item?.reference && !item?.number && !item?.title;
  };

  const formatKeyName = (key: string) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .trim()
      .replace(/^\w/, (c) => c.toUpperCase());
  };

  const aiResponse = useMemo(() => {
    if (queryResults && queryResults?.result) {
      const isAggregateItem = (item: any) => {
        return !item?.id && !item?.reference && !item?.number && !item?.title;
      };

      const uniqueResults = [...(queryResults?.result || [])].reduce(
        (acc, curr, index) => {
          const key =
            curr.number ||
            curr?.reference ||
            (isAggregateItem(curr) ? `agg_${index}` : "undefined");
          acc.set(key, curr);
          return acc;
        },
        new Map<string, AISearchResponse>(),
      );
      return Array.from(uniqueResults.values());
    }
    return [];
  }, [queryResults]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setDisplayText(""); // Instantly clear placeholder text to begin typing greeting
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  function redirect(value: any) {
    const reference = value?.reference || value?.number;
    if (!reference) return;
    const referencePrefix: string = reference?.split("-")[0];
    const mapping: Record<string, { path: string; postFix: string }> = {
      INC: { path: "incidents/view", postFix: "" },
      OBS: { path: "observations/view", postFix: "" },
      INS: { path: "inspections/view", postFix: "" },
      AUD: { path: "audits/view", postFix: "" },
      CER: { path: "compliance/personnel/certifications", postFix: "/detail" },
      REG: {
        path: "compliance/organizational/regulations",
        postFix: "/detail",
      },
    };
    const route = mapping[referencePrefix];
    if (route) {
      navigate(`/${route.path}/${value?.id}${route.postFix}`);
    }
    setSearchValue("");
    setIsFocused(false);
  }

  if (!isAiEnabled) return null;

  return (
    <div
      className="relative flex w-full max-w-md items-center cursor-pointer transition-all duration-300 ease-in-out py-1"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        className={cn(
          "inline-flex h-5 w-5 items-center justify-center text-neutral-400 transition-all duration-300",
          {
            "animate-ai-icon text-primary-500": isHovered || isFocused,
          },
        )}
      >
        <AIIcon />
      </span>
      <input
        type="text"
        className="ml-2 flex-1 border-none bg-transparent py-1 text-sm text-neutral-800 placeholder-neutral-400 outline-none focus:ring-0 cursor-text"
        placeholder={displayText}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            triggerSearch(searchValue);
          }
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />

      {isFocused && searchValue.trim().length > 0 && (
        <div
          className="absolute top-full left-0 mt-2 w-[600px] max-w-[90vw] bg-white rounded-2xl shadow-2xl border border-neutral-200 z-[9999] overflow-hidden p-4 cursor-default"
          onMouseDown={(e) => e.preventDefault()} // Prevent input blur when clicking results
        >
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <div className="flex items-center text-sm text-neutral-500 font-medium">
                <span>hmm thinking</span>
                <span className="inline-flex ml-1 gap-0.5 font-bold">
                  <span
                    className="animate-bounce"
                    style={{ animationDelay: "0s", animationDuration: "1s" }}
                  >
                    .
                  </span>
                  <span
                    className="animate-bounce"
                    style={{ animationDelay: "0.2s", animationDuration: "1s" }}
                  >
                    .
                  </span>
                  <span
                    className="animate-bounce"
                    style={{ animationDelay: "0.4s", animationDuration: "1s" }}
                  >
                    .
                  </span>
                </span>
              </div>
            </div>
          ) : isWaitingToSearch ? (
            <div className="flex flex-col gap-3 p-2">
              <div className="flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-neutral-400">
                <span>Reading...</span>
                <div className="relative w-5 h-5 rounded-full bg-amber-400 border border-amber-500 flex items-center justify-center flex-shrink-0 animate-float-emoji">
                  {/* Blinking Eyes */}
                  <div className="absolute top-1.5 left-0 right-0 flex justify-between px-1.5">
                    <div className="w-0.5 h-1.5 bg-neutral-900 rounded-full animate-blink" />
                    <div className="w-0.5 h-1.5 bg-neutral-900 rounded-full animate-blink" />
                  </div>
                  {/* Smile */}
                  <div className="absolute bottom-1 w-2.5 h-1 border-b-2 border-neutral-900 rounded-b-full" />
                </div>
              </div>
              <div className="h-6 bg-neutral-100 animate-pulse rounded w-3/4" />
              <div className="h-6 bg-neutral-100 animate-pulse rounded w-1/2" />
              <div className="h-6 bg-neutral-100 animate-pulse rounded w-5/6" />
            </div>
          ) : aiResponse && aiResponse.length > 0 ? (
            <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
              <div className="text-xs font-semibold text-neutral-400 px-2.5 mb-1">
                Intelligence Search Results
              </div>
              {aiResponse.map((query, index) => {
                if (isAggregate(query)) {
                  return (
                    <div
                      key={index}
                      className="flex flex-col gap-2 p-3 bg-neutral-50 rounded-xl border border-neutral-100 mb-2 last:mb-0"
                    >
                      {Object.entries(query).map(([key, val]) => (
                        <div
                          key={key}
                          className="flex justify-between items-center text-xs py-1 px-1.5"
                        >
                          <span className="font-semibold text-neutral-500">
                            {formatKeyName(key)}
                          </span>
                          <span className="font-bold text-neutral-800">
                            {String(val)}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                }
                return (
                  <div
                    key={query?.id || index}
                    onClick={() => redirect(query)}
                    className="grid grid-cols-4 gap-4 p-2.5 rounded-xl hover:bg-neutral-100 cursor-pointer text-xs transition-colors duration-150 border-b last:border-b-0 border-neutral-50"
                  >
                    <p className="font-semibold text-neutral-800 truncate">
                      {query?.number || query?.reference || "N/A"}
                    </p>
                    <p className="text-neutral-600 truncate col-span-2">
                      {query?.title || "N/A"}
                    </p>
                    <p className="text-neutral-500 truncate text-end">
                      {query?.dateOccured &&
                      !isNaN(Date.parse(query.dateOccured))
                        ? format(new Date(query.dateOccured), "MMM dd, yyyy")
                        : "N/A"}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-neutral-400">
              No results found for "{searchValue}"
            </div>
          )}
        </div>
      )}

      <div
        className={cn(
          "absolute bottom-0 left-0 h-0.5 transition-all duration-500 ease-in-out",
          {
            "w-full bg-primary-500": isHovered || isFocused,
            "w-full animate-underline-flash":
              !isHovered && !isFocused && shouldFlash,
            "w-0 bg-primary-500": !isHovered && !isFocused && !shouldFlash,
          },
        )}
      />
    </div>
  );
};
